import { Database, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTerritoryStore } from '@/stores/territoryStore';
import { useIngestion, MAX_INGEST_POPULATION, exceedsIngestPopulationLimit } from '@/hooks/useIngestion';

/** Libellé FR de la limite (T4.3, finding H12) — factorisé pour le composant et son test. */
export function overPopulationLimitMessage(territoryName: string): string {
  const seuilFr = MAX_INGEST_POPULATION.toLocaleString('fr-FR');
  return (
    `Les communes de plus de ${seuilFr} habitants ne sont pas encore prises en ` +
    `charge par le chargement à la demande. ${territoryName} dépasse ce seuil : le ` +
    'traitement des très grandes communes (tuilage par sous-secteurs) est un chantier ultérieur.'
  );
}

/**
 * TerritoryDataGate — bandeau global affiché quand le territoire actif ne
 * dispose que de ses données de base (has_full_data === false).
 *
 * Monté dans l'AppShell au-dessus de l'Outlet : tous les écrans data-driven
 * affichent ce panneau d'appel à l'ingestion plutôt que des données vides.
 * Dès que l'ingestion se termine, le store rafraîchit `has_full_data` (cf.
 * sync dans TerritorySelector) et ce composant se démonte automatiquement.
 */
export function TerritoryDataGate() {
  const { activeTerritory } = useTerritoryStore();
  const insee = activeTerritory?.code_insee ?? null;
  const { status, step, message, isActive, start, isStarting, startError } =
    useIngestion(insee);

  const failed = status === 'failed';
  const completed = status === 'completed';

  // T4.3 (finding H12) — limite explicite affichée PROACTIVEMENT (avant le
  // clic), en phase avec MAX_INGEST_POPULATION backend : la vraie barrière
  // reste le 422 serveur, ceci évite juste un 422 surprise à l'utilisateur.
  const overLimit = exceedsIngestPopulationLimit(activeTerritory?.population);

  // T2.2 (H5) — marqueur de complétude. `last_full_ingest_at` n'est posé par le
  // backend qu'à la toute fin du pipeline complet (bâtiments + phase 2) et est
  // invalidé (NULL) dès qu'une ré-ingestion démarre. S'il est absent alors que
  // des bâtiments existent déjà (has_full_data === true), c'est qu'une ingestion
  // a été interrompue/échouée en laissant des données PARTIELLES.
  const markerAbsent = !activeTerritory?.last_full_ingest_at;
  const partialData = activeTerritory?.has_full_data === true;
  const incomplete = failed && markerAbsent && partialData;

  // Afficher le bandeau quand : seules les données de base existent, une
  // ingestion est en cours (y compris une ré-ingestion qui remplace les
  // données), OU un échec a laissé des données incomplètes (marqueur invalidé).
  // Un territoire complet et sain (marqueur posé, aucun job actif/échoué) ne
  // déclenche rien → le composant se démonte comme avant.
  const shouldRender =
    activeTerritory?.has_full_data === false || isActive || (failed && markerAbsent);
  if (!activeTerritory || !shouldRender) return null;

  return (
    <div className="px-5 pt-5">
      <div
        role="region"
        aria-label="Chargement des données du territoire"
        className="rounded-panel border border-line bg-white p-5 shadow-card"
      >
        {/* ── En cours d'ingestion ─────────────────────────────────────────── */}
        {isActive ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Loader2 size={20} className="flex-shrink-0 animate-spin text-navy" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-display text-[15px] font-bold text-ink">
                  Chargement des données complètes de {activeTerritory.name}…
                </p>
                <p className="text-[12.5px] text-muted">
                  {step ? `Étape : ${step}` : 'Préparation…'} · environ 8 à 10 min — vous
                  pouvez continuer à naviguer.
                </p>
              </div>
            </div>
            {/* Barre de progression indéterminée */}
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-surface-subtle"
              role="progressbar"
              aria-label="Progression de l'ingestion"
            >
              <div className="tv-indeterminate h-full w-1/3 rounded-full bg-navy" />
            </div>
          </div>
        ) : completed ? (
          /* ── Terminé (transitoire avant démontage) ──────────────────────── */
          <div className="flex items-center gap-3">
            <CheckCircle2 size={20} className="flex-shrink-0 text-[#1f8a5b]" aria-hidden="true" />
            <p className="font-display text-[15px] font-bold text-ink">
              Données de {activeTerritory.name} chargées. Actualisation…
            </p>
          </div>
        ) : overLimit ? (
          /* ── Limite de taille (T4.3, finding H12) : message au lieu du bouton ── */
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-[#e1000f]" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-display text-[15px] font-bold text-ink">
                Chargement des données complètes indisponible pour {activeTerritory.name}.
              </p>
              <p className="text-[12.5px] text-muted">
                {overPopulationLimitMessage(activeTerritory.name)}
              </p>
            </div>
          </div>
        ) : failed ? (
          /* ── Échec ──────────────────────────────────────────────────────── */
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-[#e1000f]" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-display text-[15px] font-bold text-ink">
                  {incomplete
                    ? "Données incomplètes — relancez l'ingestion."
                    : 'Le chargement des données a échoué.'}
                </p>
                <p className="text-[12.5px] text-muted">
                  {incomplete
                    ? "L'ingestion a échoué en cours de route : certaines données affichées peuvent être partielles ou obsolètes. Relancez l'ingestion pour les compléter."
                    : (message ?? "Une erreur est survenue pendant l'ingestion.")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => insee && start(insee)}
              disabled={isStarting}
              className="self-start rounded-[9px] bg-navy px-4 py-2 text-[12.5px] font-semibold text-white transition hover:bg-navy-600 disabled:opacity-60"
            >
              {isStarting ? 'Relance…' : incomplete ? "Relancer l'ingestion" : 'Réessayer'}
            </button>
          </div>
        ) : (
          /* ── État initial : données non chargées ────────────────────────── */
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <Database size={20} className="mt-0.5 flex-shrink-0 text-muted" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-display text-[15px] font-bold text-ink">
                  Les données complètes de {activeTerritory.name} ne sont pas encore chargées.
                </p>
                <p className="text-[12.5px] text-muted">
                  Bâtiments, DPE, grilles et potentiel solaire sont chargés à la demande
                  (environ 8 à 10 min). La fiche reste disponible avec les données de base.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => insee && start(insee)}
              disabled={isStarting}
              className="self-start rounded-[9px] bg-navy px-4 py-2 text-[12.5px] font-semibold text-white transition hover:bg-navy-600 disabled:opacity-60"
            >
              {isStarting ? 'Lancement…' : 'Charger les données'}
            </button>
            {startError && (
              <p className="text-[12px] text-[#e1000f]">
                Impossible de lancer l'ingestion. Réessayez.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
